import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { trackWhatsApp, trackPhoneCall } from "@/components/layout/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contato() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Preencha todos os campos.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      const msg = [
        `*Nova Mensagem de Contato*`,
        ``,
        `*Nome:* ${form.name}`,
        `*Telefone:* ${form.phone}`,
        `*E-mail:* ${form.email}`,
        `*Mensagem:* ${form.message}`,
      ].join("\n");
      const waUrl = `https://wa.me/553125550207?text=${encodeURIComponent(msg)}`;
      const a = document.createElement("a");
      a.href = waUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Mensagem enviada! Abrindo WhatsApp...");
      // Google Ads conversion tracking
      if (window.gtag) {
        window.gtag('event', 'conversion', { send_to: 'AW-9363810582/contact_lead' });
        window.gtag('event', 'conversion', { send_to: 'AW-18025861296/contact_lead' });
      }
      // Meta Pixel Contact tracking
      if (window.fbq) window.fbq('track', 'Contact');
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Erro ao enviar mensagem.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contato-page">
      {/* Hero */}
      <section className="pt-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white leading-tight mb-4">
            Entre em <span className="text-gold">contato</span>
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto">
            Estamos prontos para ajudar você. Entre em contato por telefone, WhatsApp ou preencha o formulário abaixo.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="font-heading font-bold text-navy text-xl mb-5">Informações de contato</h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-navy text-sm">Belo Horizonte - MG</p>
                      <p className="text-sm text-slate-500">Rua Cura D'Ars, 1318<br />Barroca - CEP 30431-083</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-navy text-sm">Goiânia - GO</p>
                      <p className="text-sm text-slate-500">Rua 34, 90, Sl. 1001-V<br />Setor Marista - CEP 74150-220</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-navy text-sm">Telefones</p>
                      <a href="tel:+553125550207" onClick={() => trackPhoneCall('contato')} className="text-sm text-slate-500 hover:text-brand block">MG: (31) 2555-0207</a>
                      <a href="tel:+5562999642435" onClick={() => trackPhoneCall('contato')} className="text-sm text-slate-500 hover:text-brand block">GO/DF: (62) 99964-2435</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-navy text-sm">Horário</p>
                      <p className="text-sm text-slate-500">Segunda a Sexta: 7h às 19h</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-navy text-sm">E-mail</p>
                      <a href="mailto:falecom@nucleoseguros.com.br" className="text-sm text-slate-500 hover:text-brand">falecom@nucleoseguros.com.br</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="bg-[#25D366]/5 border border-[#25D366]/20 rounded-xl p-5">
                <p className="font-heading font-semibold text-navy text-sm mb-2">Atendimento por WhatsApp</p>
                <p className="text-sm text-slate-500 mb-3">Resposta rápida pelo WhatsApp.</p>
                <a href="https://wa.me/553125550207" target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp('contato')} className="btn-whatsapp inline-block text-sm px-5 py-2.5 rounded-lg" data-testid="contato-whatsapp">Falar no WhatsApp</a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border p-6 md:p-8 shadow-sm">
                <h3 className="font-heading font-semibold text-navy text-xl mb-5">Envie sua mensagem</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input placeholder="Nome completo *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" data-testid="contact-name" />
                    <Input placeholder="Telefone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" data-testid="contact-phone" />
                  </div>
                  <Input type="email" placeholder="E-mail *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="contact-email" />
                  <Textarea placeholder="Sua mensagem *" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="min-h-[120px]" data-testid="contact-message" />
                  <Button type="submit" disabled={loading} className="btn-gold w-full h-12 text-base" data-testid="contact-submit">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Enviar Mensagem</>}
                  </Button>
                </form>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="mt-12">
            <iframe
              title="Localização Núcleo Corretora"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3751.094!2d-43.9646!3d-19.9356!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDU2JzA4LjIiUyA0M8KwNTcnNTIuNiJX!5e0!3m2!1spt-BR!2sbr!4v1614183742689"
              width="100%"
              height="350"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rounded-xl"
              data-testid="google-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
